import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const endometrialBiopsy: ContentBlock<"procedure"> = {
  id: makeId("proc-endometrial-biopsy"),
  kind: "procedure",
  tags: ["clinic-visit", "her", "testing", "ivf", "fet", "pre-cycle"],
  title: "Endometrial Biopsy",
  subtitle: "Small tissue sample from the uterine lining",
  body: {
    procedureName: "Endometrial Biopsy",
    category: "diagnostic",
    description:
      "A procedure that collects a small tissue sample from the uterine lining. The sample can be used for different purposes, such as checking for chronic endometritis, pathology, or add-on tests like ERA, ALICE, EMMA, or EndomeTRIO.",
    howItWorks:
      "A speculum is placed, the cervix is cleaned, and a thin flexible catheter, often called a Pipelle, is passed through the cervix into the uterus. A small amount of endometrial tissue is gently suctioned out. Timing depends on why the biopsy is being done: chronic endometritis evaluation, general pathology, and receptivity or microbiome add-on tests may require different cycle timing.",
    duration: "10-15 minutes",
    costRange: { low: 500, high: 1200 },
    currency: "USD",
    anesthesia: "none",
    recoveryTime: "Same day, most return to normal activities immediately",
    painAndComfort: [
      "Sharp cramping lasting 10-30 seconds during the biopsy",
      "Mild to moderate menstrual-like cramping afterward",
    ],
    risks: ["Light spotting for 1-2 days", "Very rare risk of infection"],
    prepInstructions: [
      "Confirm the exact cycle day or mock-cycle timing with your clinic",
      "Ask whether you can take 600-800mg ibuprofen 30-60 minutes before the procedure",
      "Eat a light meal beforehand, do not come on an empty stomach",
      "Arrange for a ride home if you are anxious, though most patients drive themselves",
    ],
    whatToExpect: [
      "A speculum is placed and the cervix is cleaned",
      "The Pipelle catheter is inserted through the cervix, you may feel a sharp cramp lasting 10-30 seconds",
      "The tissue collection takes less than a minute",
      "Cramping similar to a menstrual cramp is normal during and after",
      "Light spotting for 1-2 days is expected",
      "Result timing depends on the lab test ordered",
      "Bring a pad or panty liner for spotting afterward",
      "Bring ibuprofen if you haven't taken it at home",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Result timing depends on what the sample is being used for. Pathology may return sooner; specialized add-on tests often take longer.",
      },
      {
        label: "What you learn",
        detail:
          "The report may look for inflammation, chronic endometritis, or test-specific findings such as receptivity timing or bacterial DNA.",
      },
      {
        label: "How it is used",
        detail:
          "How the result changes care depends on the indication. A biopsy is the sampling procedure; ERA, ALICE, EMMA, and EndomeTRIO are different tests that may use that sample.",
      },
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Biopsie endométriale",
      description:
        "Une procédure qui prélève un petit échantillon de muqueuse utérine. L'échantillon peut servir à différentes analyses, par exemple rechercher une endométrite chronique, une anomalie anatomopathologique, ou réaliser des tests additionnels comme ERA, ALICE, EMMA ou EndomeTRIO.",
      howItWorks:
        "Un spéculum est placé, le col est désinfecté, puis un cathéter fin et souple, souvent appelé Pipelle, est introduit à travers le col jusque dans l'utérus. Une petite quantité de tissu endométrial est aspirée doucement. Le timing dépend de la raison du prélèvement : recherche d'endométrite chronique, anatomopathologie, ou tests additionnels de réceptivité ou de microbiome peuvent nécessiter des moments différents du cycle.",
      duration: "10 à 15 minutes",
      recoveryTime:
        "Le jour même, la plupart reprennent leurs activités normales immédiatement",
      painAndComfort: [
        "Crampe brève (10 à 30 secondes) pendant la biopsie",
        "Crampes légères à modérées de type menstruel ensuite",
      ],
      risks: [
        "Léger spotting pendant 1 à 2 jours",
        "Risque très rare d'infection",
      ],
      prepInstructions: [
        "Confirmez avec votre clinique le jour exact du cycle ou le timing du cycle simulé",
        "Demandez si vous pouvez prendre 600 à 800 mg d'ibuprofène 30 à 60 minutes avant l'examen",
        "Mangez léger avant, ne venez pas à jeun",
        "Prévoyez quelqu'un pour vous raccompagner si vous êtes anxieuse, même si la plupart conduisent",
      ],
      whatToExpect: [
        "Un spéculum est placé et le col est désinfecté",
        "Le cathéter Pipelle est introduit à travers le col ; vous pouvez ressentir une crampe brève (10 à 30 secondes)",
        "Le prélèvement prend moins d'une minute",
        "Des crampes similaires aux règles sont normales pendant et après",
        "Un léger spotting pendant 1 à 2 jours est attendu",
        "Le délai de résultat dépend de l'analyse demandée au laboratoire",
        "Prévoyez une serviette ou un protège-slip pour le spotting",
        "Apportez de l'ibuprofène si vous n'en avez pas pris à la maison",
      ],
    },
  },
  sources: [
    {
      title: "Good practice recommendations on recurrent implantation failure",
      authors: "Cimadomo D, de los Santos MJ, Griesinger G, et al.",
      journal: "Human Reproduction Open",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37325540/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(endometrialBiopsy);

for (const testId of [
  "test-endometrial-receptivity-testing",
  "test-endometrial-microbiome-testing",
  "test-chronic-endometritis-testing",
  "test-endometrial-add-on-bundle",
]) {
  registerRelationship({
    from: endometrialBiopsy.id,
    to: makeId(testId),
    type: "related-to",
    label: "Can lead to",
  });
}
